import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import Button from '../core/Button';
import { useImportFileStore } from '@/stores/importFile';
import { IconCheck, IconWarningMessage } from '@/icons';
import {
  useImportCollectionMembers,
  useGetCollectionMembers,
  getGetCollectionMembersQueryKey,
} from '@/api/trafikportalen';
import { toast } from '@/hooks/useToast';
import { ImportState, getImportStateMeta } from '@/helpers/import';
import type { StudentType } from '@/types/students';

interface ImportConfirmProps {
  collectionId: string;
  tenantId: string;
  formType: StudentType;
}

export const ImportConfirm = ({
  collectionId,
  tenantId,
  formType,
}: ImportConfirmProps) => {
  const { validationResult, failedChanges, successChanges, importData } =
    useImportFileStore();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const importMutation = useImportCollectionMembers();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const summary = validationResult?.summary;

  const { data: collectionMembers, isLoading: membersLoading } =
    useGetCollectionMembers(collectionId);

  const stateCounts = useMemo((): { state: ImportState; count: number }[] => {
    if (!summary || membersLoading) return [];

    const updatedManuallyModifiedCount = successChanges.filter((change) => {
      if (change.action !== 'updated') return false;
      const member = collectionMembers?.find(
        (m) => m.collection_member.id === change.collection_member_id,
      );
      return member?.collection_member.manually_modified === true;
    }).length;

    const entries: { state: ImportState; count: number }[] = [
      { state: 'added', count: summary.added },
      {
        state: 'updated',
        count: summary.updated - updatedManuallyModifiedCount,
      },
      {
        state: 'updatedManuallyModified',
        count: updatedManuallyModifiedCount,
      },
      { state: 'restored', count: summary.restored },
      { state: 'removed', count: summary.removed },
      { state: 'skipped', count: summary.skipped },
      { state: 'failed', count: summary.failed },
    ];

    return entries.filter((e) => e.count > 0);
  }, [summary, successChanges, collectionMembers, membersLoading]);

  const handleSubmit = useCallback(async () => {
    // A file without rows is valid: it inactivates every student in the category.
    if (successChanges.length === 0) {
      toast.error({
        title: formatMessage({
          defaultMessage: 'Saknar data för import',
        }),
      });
      return;
    }

    setIsSubmitting(true);

    try {
      await importMutation.mutateAsync({
        collectionID: collectionId,
        data: {
          rows: importData,
          strategy: 'replace',
        },
      });

      await queryClient.invalidateQueries({
        queryKey: getGetCollectionMembersQueryKey(collectionId),
      });

      toast.success(
        {
          title: formatMessage({
            defaultMessage: 'Importen slutfördes',
          }),
        },
        { autoClose: 3000 },
      );

      const routeMap: Record<StudentType, string> = {
        travels: '/$tenantId/students/travels/$view',
        transportation: '/$tenantId/students/transportation/$view',
        'child-and-youth': '/$tenantId/students/child-and-youth/$view',
      };

      navigate({
        to: routeMap[formType],
        params: { tenantId, view: 'list' },
      });
    } catch (error) {
      toast.error({
        title: formatMessage({
          defaultMessage: 'Ett fel uppstod vid import',
        }),
      });
      console.error('Import error:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    importData,
    successChanges.length,
    collectionId,
    formatMessage,
    importMutation,
    queryClient,
    navigate,
    tenantId,
    formType,
  ]);

  if (!validationResult || !summary) {
    return (
      <div className="flex w-full items-center justify-center p-8">
        <span className="text-text-700">
          <FormattedMessage defaultMessage="Ingen valideringsdata tillgänglig" />
        </span>
      </div>
    );
  }

  return (
    <div className="mt-4 flex size-full flex-col gap-4 px-4 md:mt-8 md:px-0">
      {failedChanges.length > 0 && (
        <div
          role="alert"
          className="flex w-full flex-row items-center gap-2 rounded-md border border-red-300 bg-red-50 p-4 text-red-800"
        >
          <IconWarningMessage className="size-5" aria-hidden="true" />
          <span>
            {failedChanges.length}{' '}
            <FormattedMessage defaultMessage="misslyckades och kommer inte att importeras" />
          </span>
        </div>
      )}
      <div className="flex w-full flex-col gap-2 rounded-md border border-background-300 p-4">
        {membersLoading && (
          <span className="text-text-500">
            <FormattedMessage defaultMessage="Laddar…" />
          </span>
        )}
        {stateCounts.map(({ state, count }, index) => {
          const { icon: Icon, textColor } = getImportStateMeta(state);
          return (
            <div
              key={state}
              className={`flex flex-row items-center gap-2 ${
                index === stateCounts.length - 1 ? '' : ''
              }`}
            >
              <Icon className={`size-5 ${textColor}`} aria-hidden="true" />
              <span>
                {count}{' '}
                <FormattedMessage
                  id={`import.state.${state}${count === 1 ? '' : 'Plural'}`}
                />
              </span>
            </div>
          );
        })}
      </div>
      <Button
        size="lg"
        className="w-full"
        variant="filled"
        startIcon={<IconCheck />}
        onClick={handleSubmit}
        loading={isSubmitting}
        disabled={isSubmitting || membersLoading || successChanges.length === 0}
      >
        <FormattedMessage defaultMessage="Slutför import" />
      </Button>
    </div>
  );
};
